import {
  X,
  User,
  MapPin,
  Calendar,
  Clock,
  Smartphone,
  Monitor,
  Code,
  Camera,
  FileText,
} from 'lucide-react'
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui'
import type { AttendanceEvent } from '../../types/attendance.types'
import { APP_TIME_ZONE, formatDateTime as formatDateTimeUtil } from '../../utils/dateFormat'

interface AttendanceEventDetailsProps {
  event: AttendanceEvent | null
  open: boolean
  onClose: () => void
}

export function AttendanceEventDetails({ event, open, onClose }: AttendanceEventDetailsProps) {
  if (!open || !event) return null

  const getEventTypeBadge = (eventType: string) => {
    switch (eventType) {
      case 'check_in':
        return (
          <Badge variant="default" className="bg-green-100 text-green-800">
            Приход
          </Badge>
        )
      case 'check_out':
        return (
          <Badge variant="default" className="bg-red-100 text-red-800">
            Уход
          </Badge>
        )
      case 'break_start':
        return (
          <Badge variant="default" className="bg-yellow-100 text-yellow-800">
            Начало перерыва
          </Badge>
        )
      case 'break_end':
        return (
          <Badge variant="default" className="bg-blue-100 text-blue-800">
            Конец перерыва
          </Badge>
        )
      default:
        return <Badge variant="secondary">{eventType}</Badge>
    }
  }

  const getSourceIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'mobile':
        return <Smartphone className="h-5 w-5" />
      case 'terminal':
        return <Monitor className="h-5 w-5" />
      case 'api':
        return <Code className="h-5 w-5" />
      default:
        return null
    }
  }

  const getSourceLabel = (sourceType: string) => {
    switch (sourceType) {
      case 'mobile':
        return 'Мобильное приложение'
      case 'terminal':
        return 'Терминал'
      case 'api':
        return 'API'
      default:
        return sourceType
    }
  }

  const formatDateTime = (dateString: string) => {
    const { date, time } = formatDateTimeUtil(dateString)
    // Format as "15 марта 2024, 10:30:00"
    const dateObj = new Date(dateString)
    if (Number.isNaN(dateObj.getTime())) {
      return 'Неверная дата'
    }
    return dateObj.toLocaleString('ru-RU', {
      timeZone: APP_TIME_ZONE,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const getEmployeeName = () => {
    if (!event.employee) return 'Не указан'
    const { first_name, last_name, middle_name } = event.employee
    return [last_name, first_name, middle_name].filter(Boolean).join(' ')
  }

  const hasGPSData = () => {
    return event.lat && event.lng
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-2xl">Детали события посещаемости</CardTitle>
            <CardDescription>Информация о событии №{event.id.slice(0, 8)}</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Основная информация */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Основная информация</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Дата и время</p>
                  <p className="font-medium">{formatDateTime(event.occurred_at)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Тип события</p>
                  <div className="mt-1">{getEventTypeBadge(event.event_type)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Сотрудник */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Сотрудник</h3>
            <div className="flex items-start gap-3">
              <User className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="space-y-2 flex-1">
                <p className="font-medium text-lg">{getEmployeeName()}</p>
                {event.employee && (
                  <div className="space-y-1 text-sm text-muted-foreground">
                    {event.employee.employee_number && (
                      <p>Табельный номер: {event.employee.employee_number}</p>
                    )}
                    {event.employee.department && (
                      <p>Подразделение: {event.employee.department.name}</p>
                    )}
                    {event.employee.position && <p>Должность: {event.employee.position.name}</p>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Локация */}
          {event.location && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Локация</h3>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="space-y-2">
                  <p className="font-medium">{event.location.name}</p>
                  {event.location.address && (
                    <p className="text-sm text-muted-foreground">{event.location.address}</p>
                  )}
                  {hasGPSData() && (
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>
                        Координаты: {event.lat?.toFixed(6)}, {event.lng?.toFixed(6)}
                      </p>
                      {event.accuracy && <p>Точность GPS: ±{event.accuracy} метров</p>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Источник и метод */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Источник</h3>
            <div className="flex items-start gap-3">
              {getSourceIcon(event.source_type)}
              <div>
                <p className="text-sm text-muted-foreground">Источник</p>
                <p className="font-medium">{getSourceLabel(event.source_type)}</p>
                {event.terminal_id && (
                  <p className="text-xs text-muted-foreground mt-1">
                    ID терминала: {event.terminal_id}
                  </p>
                )}
                {event.validation_method && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Метод: {event.validation_method}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Фото */}
          {event.photo_url && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Фотография</h3>
              <div className="flex items-start gap-3">
                <Camera className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="space-y-2">
                  <img
                    src={event.photo_url}
                    alt="Фото события"
                    className="max-w-sm rounded-lg border"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Заметки */}
          {event.notes && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Заметки</h3>
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                <p className="text-sm text-muted-foreground">{event.notes}</p>
              </div>
            </div>
          )}

          {/* Информация об устройстве */}
          {event.device_info && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Информация об устройстве</h3>
              <div className="bg-muted/50 p-4 rounded-lg">
                <pre className="text-xs overflow-x-auto">{event.device_info}</pre>
              </div>
            </div>
          )}

          {/* Системная информация */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="text-lg font-semibold">Системная информация</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">ID события</p>
                <p className="font-mono text-xs">{event.id}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Дата создания</p>
                <p>{formatDateTime(event.created_at)}</p>
              </div>
              {event.updated_at && (
                <div>
                  <p className="text-muted-foreground">Последнее обновление</p>
                  <p>{formatDateTime(event.updated_at)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Действия */}
          <div className="flex justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={onClose}>
              Закрыть
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}